import { useEffect, useState } from "react";
import {
  Alert,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import useWebotServer from "../../../../hooks/useWebotServer";
import useFirestore from "../../../../hooks/useFirestore";
import type { CrmBulkScopeArgs } from "../../../../hooks/crm/useCrmBulkActions";
import CrmBulkDialog from "./CrmBulkDialog";
import { describeCrmBulkFailure, formatCount } from "./crmBulkCopy";
import { isWebotServerError } from "../../../../utils/contacts/contactWriteErrors";
import type { CrmBulkFailure } from "./crmBulkCopy";
import type { CrmContactRow, CrmContactSelection } from "../../../../@types/webotServer";
import type { REST_schema } from "../../../../@types/sharedSchema";

/**
 * Bulk "הוסף לקהל" — the CRM selection becomes (or joins) an AUDIENCE.
 *
 * ── Two modes, two capabilities ────────────────────────────────────────────────────
 *  - **קהל חדש** goes through `POST /audiences/from-selection`, which resolves the selection
 *    SERVER-SIDE — including `mode:'filter'` ("all 3,000 matching"), snapshot-verified with the
 *    `expectedCount` drift check. No enumerated-selection gate: this is the first bulk action
 *    that genuinely works on a selection the browser never held.
 *  - **קהל קיים** adds members by phone (`/audiences/:id/members/add`), so it needs the rows in
 *    hand — the same `selectedRows.length === count` gate the message dialog uses.
 *
 * ── A second host: explicit members ────────────────────────────────────────────────
 * The listing's matched-buyers table (`ListingMatchesTable`) hands over `explicitMembers` — the
 * rows it already holds, own AND colleagues' — instead of a CRM selection. Its NEW mode then
 * creates the audience from those members (`POST /audiences`), because `from-selection` resolves
 * ONE CRM scope per request: a colleague's row under `view:'mine'` fails the whole request, and
 * colleagues from two agencies cannot be expressed at all. /crm's call sites pass no
 * `explicitMembers` and keep the path above exactly.
 */

/** "לקוחות · 19/08 14:30" — dated, because a realtor makes several of these. */
const defaultAudienceName = (): string => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `לקוחות · ${pad(now.getDate())}/${pad(now.getMonth() + 1)} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

type Props = {
  open: boolean;
  count: number;
  selectedRows: CrmContactRow[];
  /** The CRM selection (/crm). Required unless `explicitMembers` is given. */
  buildSelection?: () => CrmContactSelection;
  scope?: CrmBulkScopeArgs;
  /**
   * The members themselves, already resolved by the host (the listing matches table). Both modes
   * then write exactly these — see the header. Frozen by the host when the dialog opens, so the
   * count on screen stays the count acted on.
   */
  explicitMembers?: { members: REST_schema.WhatsappGroupMemberInput[]; description?: string };
  /** The name the "new audience" field starts with. Default: "לקוחות · DD/MM HH:MM". */
  defaultName?: string;
  /** A line the host needs the realtor to read before confirming (e.g. who was left out). */
  notice?: string;
  onClose: () => void;
  onCompleted: (summary: string, result?: { groupId: string }) => void;
};

/** Any Hebrew letter — see `describeAudienceFailure`. */
const HEBREW = /[\u0590-\u05FF]/;

/**
 * The group routes answer `{ error: <Hebrew sentence>, code }`, which the client reads as
 * `code = <the sentence>` with no `serverMessage` — so the shared describer fell back to a generic
 * line and hid a reason the realtor can act on ("קהל יכול להכיל עד 5,000…"). Hebrew in `code` IS
 * the server's message.
 */
const describeAudienceFailure = (error: unknown): CrmBulkFailure => {
  if (isWebotServerError(error) && !error.serverMessage && typeof error.code === "string" && HEBREW.test(error.code)) {
    return { message: error.code, requiresReview: false };
  }
  return describeCrmBulkFailure(error, "message");
};

export default function CrmBulkAddToAudienceDialog({
  open,
  count,
  selectedRows,
  buildSelection,
  scope,
  explicitMembers,
  defaultName,
  notice,
  onClose,
  onCompleted
}: Props) {
  const { audiencesList, audiencesCreate, audiencesCreateFromSelection, audiencesMembersAdd } = useWebotServer();
  const { organizations } = useFirestore();

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<CrmBulkFailure | null>(null);
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [shareOrgId, setShareOrgId] = useState("");
  const [audiences, setAudiences] = useState<REST_schema.WhatsappGroupView[] | null>(null);
  const [targetAudienceId, setTargetAudienceId] = useState("");

  const orgs = organizations ?? [];
  const effectiveShareOrgId = shareOrgId || orgs[0]?.id || "";

  useEffect(() => {
    if (!open) return;
    setFailure(null);
    setMode("new");
    setName(defaultName || defaultAudienceName());
    setShared(false);
    setShareOrgId("");
    setTargetAudienceId("");
    audiencesList()
      .then((res) => setAudiences(res.groups))
      .catch(() => setAudiences([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Explicit members ARE the whole audience, in hand by construction.
  const audienceComplete = explicitMembers ? true : selectedRows.length === count;
  const existingBlocked = mode === "existing" && !audienceComplete;

  const confirm = async () => {
    setFailure(null);
    setBusy(true);
    try {
      if (mode === "new") {
        const audienceName = name.trim() || defaultName || defaultAudienceName();
        const response = explicitMembers
          ? await audiencesCreate({
              name: audienceName,
              members: explicitMembers.members,
              source: "crm_selection",
              ...(explicitMembers.description ? { description: explicitMembers.description } : {}),
              // `scope`/`orgId` on POST /audiences are the SHARING org — never a row's CRM scope.
              ...(shared && effectiveShareOrgId ? { scope: "org" as const, orgId: effectiveShareOrgId } : {})
            })
          : await audiencesCreateFromSelection({
              name: audienceName,
              selection: (buildSelection as () => CrmContactSelection)(),
              view: scope?.view,
              orgId: scope?.orgId,
              ...(shared && effectiveShareOrgId ? { audienceScope: "org" as const, audienceOrgId: effectiveShareOrgId } : {})
            });
        const dropped =
          response.invalidRows.length > 0 ? ` · ${formatCount(response.invalidRows.length)} מספרים לא תקינים דולגו` : "";
        onCompleted(`נוצר קהל עם ${formatCount(response.memberCount)} לקוחות${dropped}`, { groupId: response.groupId });
        onClose();
      } else {
        const members: REST_schema.WhatsappGroupMemberInput[] =
          explicitMembers?.members ??
          selectedRows.map((row) => ({
            phone: row.contactId,
            firstName: row.firstName ?? undefined,
            lastName: row.lastName ?? undefined,
            displayName: row.name ?? undefined,
            contactId: row.contactId,
            ownerUserId: row.ownerUserId
          }));
        const response = await audiencesMembersAdd(targetAudienceId, members);
        onCompleted(`הקהל מונה עכשיו ${formatCount(response.memberCount)} נמענים`, { groupId: response.groupId });
        onClose();
      }
    } catch (error) {
      setFailure(describeAudienceFailure(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmBulkDialog
      open={open}
      title="הוסף לקהל"
      count={count}
      confirmLabel={mode === "new" ? "יצירת קהל" : "הוספה לקהל"}
      busy={busy}
      failure={failure}
      confirmDisabled={existingBlocked || (mode === "existing" && !targetAudienceId) || (mode === "new" && !name.trim())}
      onConfirm={confirm}
      onClose={onClose}
    >
      <Typography variant="body2">
        הלקוחות שנבחרו יישמרו בקהל — רשימת נמענים שאפשר לשלוח אליה קמפיין בכל רגע, מהאשף או
        מעמוד הקהלים.
      </Typography>
      {notice && <Alert severity="info">{notice}</Alert>}

      <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as "new" | "existing")}>
        <FormControlLabel value="new" control={<Radio size="small" />} label="קהל חדש" />
        <FormControlLabel
          value="existing"
          control={<Radio size="small" />}
          label="הוספה לקהל קיים"
          disabled={(audiences?.length ?? 0) === 0}
        />
      </RadioGroup>

      {mode === "new" ? (
        <Stack spacing={1.5}>
          <TextField fullWidth size="small" label="שם הקהל" value={name} onChange={(event) => setName(event.target.value)} />
          {orgs.length > 0 && (
            <>
              <FormControlLabel
                control={<Switch size="small" checked={shared} onChange={(event) => setShared(event.target.checked)} />}
                label="שיתוף עם המשרד"
              />
              {shared && orgs.length > 1 && (
                <TextField
                  select
                  size="small"
                  label="משרד"
                  value={effectiveShareOrgId}
                  onChange={(event) => setShareOrgId(event.target.value)}
                  fullWidth
                >
                  {orgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name ?? org.id}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
        </Stack>
      ) : (
        <TextField
          select
          size="small"
          label="קהל"
          value={targetAudienceId}
          onChange={(event) => setTargetAudienceId(event.target.value)}
          fullWidth
        >
          {(audiences ?? []).map((audience) => (
            <MenuItem key={audience.id} value={audience.id}>
              {audience.name} ({audience.memberCount})
            </MenuItem>
          ))}
        </TextField>
      )}

      {existingBlocked && (
        <Alert severity="warning">
          הוספה לקהל קיים דורשת שכל הלקוחות שנבחרו יהיו טעונים במסך — כרגע טעונים{" "}
          {formatCount(selectedRows.length)} מתוך {formatCount(count)}. לחצו על "הצג עוד" עד
          שכולם ייטענו, או בחרו "קהל חדש" (שנוצר מהשרת וכולל את כל התוצאות).
        </Alert>
      )}
    </CrmBulkDialog>
  );
}
